import { useEffect, useState } from "react";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { useBrand } from "@/context/BrandContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Save, KeyRound, Palette, FileText, Settings as SettingsIcon } from "lucide-react";
import { ApiTokensCard } from "@/pages/settings/ApiTokensCard";
import { CmsIntegrationCard } from "@/pages/settings/CmsIntegrationCard";
import { DnsIntegrationCard } from "@/pages/settings/DnsIntegrationCard";
import { OneTrustIntegrationCard } from "@/pages/settings/OneTrustIntegrationCard";
import { DealerFeedImportCard } from "@/pages/settings/DealerFeedImportCard";
import { PhotoUploadSettingsCard } from "@/pages/settings/PhotoUploadSettingsCard";
import { RestartApplicationCard } from "@/pages/settings/RestartApplicationCard";
import { DealerHostSuffixCard } from "@/pages/settings/DealerHostSuffixCard";
import { KeycloakSettingsCard } from "@/pages/settings/KeycloakSettingsCard";
import { MdHashSettingsCard } from "@/pages/settings/MdHashSettingsCard";
import { ExternalIpCard } from "@/pages/settings/ExternalIpCard";
import { PERM, hasGlobalPermission } from "@/lib/permissions";

export default function Settings() {
  const { user } = useAuth();
  const { t } = useI18n();
  const { refresh } = useBrand();

  const isSystemAdmin = hasGlobalPermission(user, PERM.SYSTEM_BRANDING_MANAGE);
  const canManageCookieConsents = hasGlobalPermission(user, PERM.COOKIE_CONSENTS_MANAGE);

  const [gmapsKey, setGmapsKey] = useState("");
  const [brandName, setBrandName] = useState("");
  const [brandLogo, setBrandLogo] = useState("");
  const [brandColor, setBrandColor] = useState("");
  const [brandSecondaryColor, setBrandSecondaryColor] = useState("");
  const [imprintPath, setImprintPath] = useState("");
  const [privacyPath, setPrivacyPath] = useState("");
  const [cookiePolicyPath, setCookiePolicyPath] = useState("");
  const [dataChangeRequestEmail, setDataChangeRequestEmail] = useState("");
  const [dataChangeRequestCcEmail, setDataChangeRequestCcEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get(`/settings`);
        const map = Object.fromEntries(data.map((s) => [s.key, s.value]));
        setGmapsKey(map.google_maps_api_key || "");
        setBrandName(map.brand_name || "");
        setBrandLogo(map.brand_logo_url || "");
        setBrandColor(map.brand_primary_color || "");
        setBrandSecondaryColor(map.brand_secondary_color || "");
        setImprintPath(map.imprint_path || "");
        setPrivacyPath(map.privacy_path || "");
        setCookiePolicyPath(map.cookie_policy_path || "");
        setDataChangeRequestEmail(map.data_change_request_email || "");
        setDataChangeRequestCcEmail(map.data_change_request_cc_email || "");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const saveKey = async (key, value) => {
    await client.put(`/settings/${key}`, { value });
  };

  const saveGmaps = async () => {
    setSaving(true);
    try {
      await saveKey("google_maps_api_key", gmapsKey);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const saveLegalPaths = async () => {
    setSaving(true);
    try {
      await Promise.all([
        saveKey("imprint_path", imprintPath),
        saveKey("privacy_path", privacyPath),
        saveKey("cookie_policy_path", cookiePolicyPath),
      ]);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const saveOtherSettings = async () => {
    setSaving(true);
    try {
      await Promise.all([
        saveKey("data_change_request_email", dataChangeRequestEmail),
        saveKey("data_change_request_cc_email", dataChangeRequestCcEmail),
      ]);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const saveBrand = async () => {
    setSaving(true);
    try {
      await Promise.all([
        saveKey("brand_name", brandName),
        saveKey("brand_logo_url", brandLogo),
        saveKey("brand_primary_color", brandColor),
        saveKey("brand_secondary_color", brandSecondaryColor),
      ]);
      await refresh();
      toast.success("Branding updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl p-6 md:p-10" data-testid="settings-page">
      <div className="mb-8">
        <p className="data-label mb-2">SYSTEM · CONFIGURATION</p>
        <h1 className="text-3xl font-bold tracking-tight md:text-4xl">{t("settings.title")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("settings.subtitle")}</p>
      </div>

      {/* Google Maps API key */}
      <Card className="mb-6 p-6" data-testid="settings-gmaps-card">        <div className="mb-4 flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-lg font-semibold">{t("settings.googleMapsKey")}</h2>
        </div>
        <p className="mb-4 text-xs text-muted-foreground">{t("settings.googleMapsSub")}</p>
        <div className="flex gap-2">
          <Input
            data-testid="settings-gmaps-input"
            placeholder="AIza…"
            value={gmapsKey}
            onChange={(e) => setGmapsKey(e.target.value)}
            disabled={loading || saving}
            className="font-mono text-xs"
          />
          <Button
            data-testid="settings-gmaps-save"
            onClick={saveGmaps}
            disabled={loading || saving}
            className="gap-1.5"
          >
            <Save className="h-3.5 w-3.5" />
            {t("common.save")}
          </Button>
        </div>
      </Card>

      {/* External API tokens (MHQ Power Admin only — same gating as GMaps key) */}
      <ApiTokensCard />

      {/* CMS push webhook / status URL / API key */}
      <CmsIntegrationCard />

      {/* Dealer feed import (JSON + XML) — System Administrator only */}
      <DealerFeedImportCard />

      {/* Branch/employee photo upload limits (max size, allowed formats, thumbnail size) */}
      <PhotoUploadSettingsCard />

      {/* Dealer DNS setup wizard — Cloudflare + Azure App Service credentials */}
      <DnsIntegrationCard />

      {/* OneTrust Cookie Consent — domain provisioning/reconciliation credentials.
          Gated on the same permission as the dealer-detail-page actions
          (CookieConsentsCard's "Queue Setup"/"Create OneTrust Record"), not just
          the page-level Settings permission, since they're a different grant. */}
      {canManageCookieConsents && <OneTrustIntegrationCard />}

      {/* Legal page paths — used to dynamically build each dealer's Imprint/
          Privacy/Cookie-Policy URLs from its URL host, e.g. "https://" +
          url_host + "." + dealer_host_suffix + "/" + path */}
      <Card className="mb-6 p-6" data-testid="settings-legal-paths-card">
        <div className="mb-4 flex items-center gap-2">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-lg font-semibold">{t("settings.legalPages")}</h2>
        </div>
        <p className="mb-4 text-xs text-muted-foreground">{t("settings.legalPagesSub")}</p>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <Label className="data-label mb-1 block">{t("settings.imprintPath")}</Label>
            <Input
              data-testid="settings-imprint-path"
              value={imprintPath}
              onChange={(e) => setImprintPath(e.target.value)}
              placeholder="impressum"
              disabled={loading || saving}
              className="font-mono text-xs"
            />
          </div>
          <div>
            <Label className="data-label mb-1 block">{t("settings.privacyPath")}</Label>
            <Input
              data-testid="settings-privacy-path"
              value={privacyPath}
              onChange={(e) => setPrivacyPath(e.target.value)}
              placeholder="datenschutz"
              disabled={loading || saving}
              className="font-mono text-xs"
            />
          </div>
          <div>
            <Label className="data-label mb-1 block">{t("settings.cookiePolicyPath")}</Label>
            <Input
              data-testid="settings-cookie-policy-path"
              value={cookiePolicyPath}
              onChange={(e) => setCookiePolicyPath(e.target.value)}
              placeholder="cookies"
              disabled={loading || saving}
              className="font-mono text-xs"
            />
          </div>
        </div>
        <div className="mt-6 flex justify-end">
          <Button
            data-testid="settings-legal-paths-save"
            onClick={saveLegalPaths}
            disabled={loading || saving}
            className="gap-1.5"
          >
            <Save className="h-3.5 w-3.5" />
            {t("common.save")}
          </Button>
        </div>
      </Card>

      {/* Other Settings */}
      <Card className="mb-6 p-6" data-testid="settings-other-card">
        <div className="mb-4 flex items-center gap-2">
          <SettingsIcon className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-lg font-semibold">{t("settings.otherSettings")}</h2>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <Label className="data-label mb-1 block">{t("settings.dataChangeRequestEmail")}</Label>
            <Input
              data-testid="settings-data-change-request-email"
              type="email"
              value={dataChangeRequestEmail}
              onChange={(e) => setDataChangeRequestEmail(e.target.value)}
              placeholder="dealer-changes@skoda-auto.de"
              disabled={loading || saving}
              className="font-mono text-xs"
            />
          </div>
          <div>
            <Label className="data-label mb-1 block">{t("settings.dataChangeRequestCcEmail")}</Label>
            <Input
              data-testid="settings-data-change-request-cc-email"
              type="email"
              value={dataChangeRequestCcEmail}
              onChange={(e) => setDataChangeRequestCcEmail(e.target.value)}
              placeholder="dealer-changes-cc@skoda-auto.de"
              disabled={loading || saving}
              className="font-mono text-xs"
            />
          </div>
        </div>
        <div className="mt-6 flex justify-end">
          <Button
            data-testid="settings-other-save"
            onClick={saveOtherSettings}
            disabled={loading || saving}
            className="gap-1.5"
          >
            <Save className="h-3.5 w-3.5" />
            {t("common.save")}
          </Button>
        </div>
      </Card>

      {/* Branding (System Administrator only) */}
      <Card
        className={`p-6 ${!isSystemAdmin ? "opacity-60" : ""}`}
        data-testid="settings-brand-card"
      >
        <div className="mb-4 flex items-center gap-2">
          <Palette className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-lg font-semibold">{t("settings.brand")}</h2>
        </div>
        <p className="mb-4 text-xs text-muted-foreground">
          {t("settings.brandSub")}
          {!isSystemAdmin && (
            <span className="ml-2 rounded-full border border-border bg-muted px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest">
              System Administrator only
            </span>
          )}
        </p>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="md:col-span-2">
            <Label className="data-label mb-1 block">{t("settings.brandName")}</Label>
            <Input
              data-testid="settings-brand-name"
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              disabled={!isSystemAdmin || loading || saving}
            />
          </div>
          <div>
            <Label className="data-label mb-1 block">{t("settings.brandColor")}</Label>
            <Input
              data-testid="settings-brand-color"
              value={brandColor}
              onChange={(e) => setBrandColor(e.target.value)}
              placeholder="#2563EB"
              disabled={!isSystemAdmin || loading || saving}
              className="font-mono"
            />
          </div>
          <div>
            <Label className="data-label mb-1 block">{t("settings.brandSecondaryColor")}</Label>
            <Input
              data-testid="settings-brand-secondary-color"
              value={brandSecondaryColor}
              onChange={(e) => setBrandSecondaryColor(e.target.value)}
              placeholder="#0F362E"
              disabled={!isSystemAdmin || loading || saving}
              className="font-mono"
            />
          </div>
          <div className="md:col-span-3">
            <Label className="data-label mb-1 block">{t("settings.brandLogo")}</Label>
            <Input
              data-testid="settings-brand-logo"
              value={brandLogo}
              onChange={(e) => setBrandLogo(e.target.value)}
              placeholder="https://…/logo.svg"
              disabled={!isSystemAdmin || loading || saving}
              className="font-mono text-xs"
            />
          </div>
        </div>
        <div className="mt-6 flex justify-end">
          <Button
            data-testid="settings-brand-save"
            onClick={saveBrand}
            disabled={!isSystemAdmin || loading || saving}
            className="gap-1.5"
          >
            <Save className="h-3.5 w-3.5" />
            {t("common.save")}
          </Button>
        </div>
      </Card>

      {/* Dealer host suffix (System Administrator only) — the domain every
          dealer site lives under, e.g. "skoda-auto.de" */}
      <DealerHostSuffixCard />

      {/* External IP lookup (System Administrator only — the card hides itself otherwise) */}
      <ExternalIpCard />

      {/* MD hash secret key + IV (System Administrator only — the card hides itself otherwise) */}
      <MdHashSettingsCard />

      {/* Keycloak admin client credentials (System Administrator only — the card hides itself otherwise) */}
      <KeycloakSettingsCard />

      {/* Restart Application (System Administrator only) — kept last on the page */}
      <div className="mt-6">
        <RestartApplicationCard />
      </div>
    </div>
  );
}
